import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getNationalitiesCached, getStoredNationalities } from '../../../api/constantsServices';

/**
 * Redux slice for nationalities data
 * Fetches and caches nationality list from API
 */

// Async thunk to fetch nationalities
export const fetchNationalitiesList = createAsyncThunk(
  'nationalities/fetchList',
  async (options = {}, { rejectWithValue }) => {
    try {
      const result = await getNationalitiesCached(options);

      if (result.success) {
        return result.data;
      } else {
        return rejectWithValue(result.error || 'Failed to fetch nationalities');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch nationalities');
    }
  }
);

const nationalitiesSlice = createSlice({
  name: 'nationalities',
  initialState: {
    // Seeded from the list an earlier page load saved, so a form can show it
    // on its first render; [] (never null) when there is none.
    data: getStoredNationalities(),
    loading: false,
    error: null,
    lastFetched: null,
  },
  reducers: {
    // Clear nationalities data
    clearNationalities: (state) => {
      state.data = [];
      state.error = null;
      state.lastFetched = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNationalitiesList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNationalitiesList.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
        state.error = null;
        state.lastFetched = Date.now();
      })
      .addCase(fetchNationalitiesList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch nationalities';
      });
  },
});

// Actions
export const { clearNationalities } = nationalitiesSlice.actions;

// Selectors
export const selectNationalities = (state) => state.nationalities?.data || [];
export const selectNationalitiesLoading = (state) => state.nationalities?.loading || false;
export const selectNationalitiesError = (state) => state.nationalities?.error || null;
export const selectNationalitiesLastFetched = (state) => state.nationalities?.lastFetched || null;

export default nationalitiesSlice.reducer;
